import React from "react";
import { useNavigate } from "react-router-dom";
import { ScanSearch, FileSearch } from "lucide-react";
import { SectionFrame } from "./SectionFrame";
import { StatusBadge } from "../../StatusBadge";

/** Monthly Record - Timecard/OCR/Corrections section (spec P0-C). Reuses the SAME
 * timekeeping month-review payload already fetched once by MonthlyRecordTab - no second
 * timekeeping business-logic implementation, and no duplicate network call (this section
 * and ApprovedAttendanceSection below both read from the one shared fetch). */
export const TimecardSection = ({ employeeId, data, loading, error, onRetry }) => {
  const navigate = useNavigate();
  const records = data?.daily_records || [];
  const corrections = records.filter((r) => (r.correction_ids || []).length > 0).length;
  const exceptions = records.filter((r) => r.classification && r.classification !== "MATCH").length;

  return (
    <SectionFrame
      title="Timecard / OCR / Corrections" icon={ScanSearch} testId="monthly-timecard-section"
      loading={loading} error={error} onRetry={onRetry}
      isEmpty={records.length === 0 && !data?.pair}
      emptyMessage="No timecard scans have been imported for this period yet."
    >
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center mb-4">
        <div className="bg-slate-50 rounded-lg p-3" data-testid="monthly-timecard-source-status">
          <p className="text-sm font-bold text-slate-900">{data?.blue_file && data?.red_file ? "2/2" : data?.blue_file || data?.red_file ? "1/2" : "0/2"}</p>
          <p className="text-[11px] text-slate-500 uppercase">BLUE/RED scans</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3" data-testid="monthly-timecard-days-count">
          <p className="text-sm font-bold text-slate-900">{records.length}</p>
          <p className="text-[11px] text-slate-500 uppercase">Days recorded</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3" data-testid="monthly-timecard-corrections-count">
          <p className="text-sm font-bold text-slate-900">{corrections}</p>
          <p className="text-[11px] text-slate-500 uppercase">Corrections</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-3" data-testid="monthly-timecard-exceptions-count">
          <p className="text-sm font-bold text-slate-900">{exceptions}</p>
          <p className="text-[11px] text-slate-500 uppercase">Exceptions</p>
        </div>
      </div>
      <div className="flex items-center justify-between">
        <StatusBadge status={data?.official_record_status || "SOURCE_PENDING"} />
        <button
          data-testid="monthly-open-full-timecard-review-button"
          onClick={() => navigate(`/management/timekeeping/review/${employeeId}?payroll_month=${data?.payroll_month}`)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 active:scale-95 transition-all"
        >
          <FileSearch size={14} /> Open Full Timekeeping Review
        </button>
      </div>
    </SectionFrame>
  );
};
